(() => {
  'use strict';
  if (window.C1VideoLifecycleFix) return;
  window.C1VideoLifecycleFix = true;

  const designMode = () => Boolean(window.Shopify && Shopify.designMode);
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const signature = (video) => {
    if (!(video instanceof HTMLVideoElement)) return '';
    const sources = [...video.querySelectorAll('source')]
      .map((source) => `${source.getAttribute('src') || ''}|${source.getAttribute('type') || ''}`)
      .join('||');
    return `${video.getAttribute('src') || ''}###${sources}###${video.getAttribute('poster') || ''}`;
  };

  const pause = (video, reset = false) => {
    if (!(video instanceof HTMLVideoElement)) return;
    try { video.pause(); } catch (error) {}
    if (reset) {
      try { video.currentTime = 0; } catch (error) {}
    }
  };

  const load = (video) => {
    if (!(video instanceof HTMLVideoElement)) return;
    try { video.load(); } catch (error) {}
  };

  const play = (video) => {
    if (!(video instanceof HTMLVideoElement) || reducedMotion()) return;
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') promise.catch(() => {});
  };

  const isActivePanel = (panel) => Boolean(
    panel && !panel.hidden && panel.getAttribute('aria-hidden') !== 'true'
  );

  const refreshVideo = (video, force = false) => {
    if (!(video instanceof HTMLVideoElement)) return;
    const current = signature(video);
    const previous = video.dataset.c1LifecycleSource || '';
    const changed = Boolean(previous && current && previous !== current);

    if (force || changed) {
      pause(video, true);
      if (video.matches('[data-c1-lazy-video]')) video.preload = 'none';
      load(video);
    }

    video.dataset.c1LifecycleSource = current;
  };

  const syncTabs = (tabs, { restartActive = false, forceReload = false } = {}) => {
    if (!(tabs instanceof Element)) return;

    tabs.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
      const active = isActivePanel(panel);
      panel.querySelectorAll('video').forEach((video) => {
        if (!active) {
          pause(video);
          return;
        }

        refreshVideo(video, forceReload);
        if (restartActive) pause(video, true);

        if (video.matches('[data-c1-lazy-video]')) {
          if (window.C1LazyVideoController) window.C1LazyVideoController.sync(video);
          else pause(video);
          return;
        }
        if (video.dataset.c1Autoplay === 'true' || video.autoplay) play(video);
      });
    });
  };

  const initTabRoot = (tabs) => {
    if (!(tabs instanceof Element)) return;
    if (tabs.dataset.c1VideoLifecycleBound === 'true') {
      syncTabs(tabs);
      return;
    }
    tabs.dataset.c1VideoLifecycleBound = 'true';

    const scheduleSync = () => requestAnimationFrame(() => {
      syncTabs(tabs, { restartActive: true, forceReload: designMode() });
    });

    tabs.addEventListener('click', (event) => {
      if (event.target.closest?.('[role="tab"]')) scheduleSync();
    });

    tabs.addEventListener('keydown', (event) => {
      if (!event.target.closest?.('[role="tab"]')) return;
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
        setTimeout(scheduleSync, 0);
      }
    });

    syncTabs(tabs);
  };

  const collect = (root, selector) => {
    const result = [];
    if (root instanceof Element && root.matches(selector)) result.push(root);
    root?.querySelectorAll?.(selector).forEach((item) => result.push(item));
    return result;
  };

  const refresh = (root = document, force = false) => {
    collect(root, 'video').forEach((video) => refreshVideo(video, force && designMode()));
    collect(root, '[data-c1-tabs]').forEach(initTabRoot);
    collect(root, '[data-c1-tabs]').forEach((tabs) => syncTabs(tabs, {
      restartActive: force && designMode(),
      forceReload: force && designMode()
    }));
  };

  let editorObserver = null;
  let editorTimer = null;
  const installEditorObserver = () => {
    if (!designMode() || editorObserver || !('MutationObserver' in window)) return;
    const target = document.getElementById('MainContent') || document.body;
    if (!target) return;

    const queue = (node) => {
      clearTimeout(editorTimer);
      editorTimer = setTimeout(() => {
        const scope = node instanceof Element ? (node.closest('.shopify-section') || node) : document;
        refresh(scope, true);
      }, 70);
    };

    editorObserver = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === 'attributes') {
          if ((record.target instanceof HTMLVideoElement || record.target instanceof HTMLSourceElement) && record.attributeName === 'src') {
            queue(record.target);
            return;
          }
        }

        if (record.type === 'childList') {
          const changed = [...record.addedNodes, ...record.removedNodes].some((node) => {
            if (!(node instanceof Element)) return false;
            return node.matches?.('video, source, [data-c1-tabs], [role="tabpanel"]') ||
              node.querySelector?.('video, source, [data-c1-tabs], [role="tabpanel"]');
          });
          if (changed) {
            queue(record.target);
            return;
          }
        }
      }
    });

    editorObserver.observe(target, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['src']
    });
  };

  const boot = () => {
    refresh(document, false);
    installEditorObserver();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target || document;
    refresh(root, false);
    if (designMode()) setTimeout(() => refresh(root, true), 50);
  });

  document.addEventListener('shopify:block:select', (event) => {
    if (!designMode()) return;
    const root = event.target?.closest?.('.shopify-section') || event.target || document;
    setTimeout(() => refresh(root, true), 50);
  });
})();
